import React, { useEffect, useRef, useState } from 'react';
import { T } from '@/theme/tokens';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { getUser, setUserSession } from '@/lib/common';

interface CurrencyMenuProps {
  currency: string;
}

export default function CurrencyMenu({ currency }: CurrencyMenuProps) {
  const [open, setOpen] = useState(false);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selected = currency || 'USD';

  useEffect(() => {
    if (!open || currencies.length > 0 || loading) return;
    setLoading(true);
    fetchWrapper
      .get(urls.DevOps.currencies)
      .then((rows: any[]) => {
        const codes = (rows ?? [])
          .map((row: any) => row?.currency ?? row)
          .filter((code: any): code is string => typeof code === 'string' && code.length > 0);
        setCurrencies(codes);
      })
      .catch((error) => console.error('Failed to fetch currencies:', error))
      .finally(() => setLoading(false));
  }, [open, currencies.length, loading]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selectCurrency = async (code: string) => {
    setOpen(false);
    const user = getUser();
    if (!user || code === selected || saving) return;
    setSaving(true);
    try {
      await fetchWrapper.put(`${urls.DevOps.updateUserPassword}${user.id}`, {
        default_currency: code,
      });
      setUserSession({ ...user, default_currency: code });
    } catch (error) {
      console.error('Failed to update currency:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        title="Display currency"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={saving}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: '#fff',
          border: `1px solid ${T.borderInput}`,
          borderRadius: 9,
          padding: 3,
          cursor: saving ? 'default' : 'pointer',
          opacity: saving ? 0.6 : 1,
          font: 'inherit',
        }}>
        <span
          style={{
            padding: '6px 13px',
            borderRadius: 7,
            fontWeight: 700,
            fontSize: 12.5,
            background: T.primary,
            color: '#fff',
          }}>
          {selected}
        </span>
        <span
          aria-hidden
          style={{
            paddingRight: 7,
            fontSize: 9,
            color: T.textFaint,
            transform: open ? 'rotate(180deg)' : 'none',
          }}>
          &#9660;
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Display currency"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 1400,
            minWidth: 120,
            maxHeight: 280,
            overflowY: 'auto',
            background: '#fff',
            border: `1px solid ${T.border}`,
            borderRadius: 9,
            boxShadow: T.popShadow,
            padding: 5,
          }}>
          {loading ? (
            <div style={{ padding: '8px 12px', fontSize: 12.5, color: T.textFaint }}>
              Loading…
            </div>
          ) : currencies.length === 0 ? (
            <div style={{ padding: '8px 12px', fontSize: 12.5, color: T.textFaint }}>
              No currencies
            </div>
          ) : (
            currencies.map((code) => {
              const isSelected = code === selected;
              return (
                <button
                  key={code}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => selectCurrency(code)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '7px 12px',
                    border: 'none',
                    borderRadius: 7,
                    background: isSelected ? T.primaryTint : 'transparent',
                    color: isSelected ? T.primary : T.textMut,
                    fontWeight: isSelected ? 700 : 600,
                    fontSize: 12.5,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(event) => {
                    if (!isSelected) event.currentTarget.style.background = T.neutralTint;
                  }}
                  onMouseLeave={(event) => {
                    if (!isSelected) event.currentTarget.style.background = 'transparent';
                  }}>
                  {code}
                  {isSelected && <span aria-hidden>&#10003;</span>}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
